<template>
    <div id="client-subscription-component">
        <SubscriptionConfirmationDialogComponent
            v-model="dialogStopRenewal"
            :title="$t('subscription_page.stop_automatic_renewal')"
            :message="$t('subscription_page.confirm_stop_renewal_message', [selectedDeviceName, selectedDeviceNumber])"
            :sub-message="$t('subscription_page.message_redirect_alert')"
            :loading="loading_action"
            variant="danger"
            @confirm="confirmStopAutomaticRenewal"
            @cancel="closeStopRenewalDialog"
        />

        <SubscriptionConfirmationDialogComponent
            v-model="dialogRenew"
            :title="$t('subscription_page.renew_automatically')"
            :message="$t('subscription_page.confirm_renew_subscription', [selectedDeviceName, selectedDevicePrice])"
            :sub-message="$t('subscription_page.message_redirect_alert')"
            :loading="loading_action"
            @confirm="confirmRenewSubscription"
            @cancel="closeRenewDialog"
        />

        <SubscriptionConfirmationDialogComponent
            v-model="dialogResume"
            :title="$t('subscription_page.resume_subscription')"
            :message="$t('subscription_page.confirm_resume_subscription', [selectedDeviceName, selectedDate, selectedDevicePrice])"
            :sub-message="$t('subscription_page.message_redirect_alert')"
            :loading="loading_action"
            @confirm="confirmResumeSubscription"
            @cancel="closeResumeDialog"
        />

        <SubscriptionConfirmationDialogComponent
            v-model="dialogChangeBankDetails"
            :title="$t('subscription_page.change_bank_details')"
            :message="$t('subscription_page.confirm_change_bank_details')"
            :loading="loading_action"
            @confirm="confirmChangeBankDetails"
            @cancel="closeChangeBankDetailsDialog"
        />

        <SubscriptionConfirmationDialogComponent
            v-model="dialogHistoryDetails"
            :title="$t('subscription_page.history')"
            :message="$t('subscription_page.history_details_message')"
            :sub-message="$t('subscription_page.message_redirect_alert')"
            :loading="loading_action"
            @confirm="confirmViewHistoryDetails"
            @cancel="closeViewHistoryDialog"
        />

        <div class="ml-2 primary--text pa-4">
            <span v-if="activeSubscriptionsCount !== null && activeSubscriptionsCount > 0">
                {{ $tc('subscription_page.you_have_active_subscriptions', activeSubscriptionsCount) }}
            </span>
        </div>

        <div class="d-flex justify-space-between pa-4" v-if="hasSubscription">
            <v-btn class="text--white" elevation="0" color="primary" rounded @click="openChangeBankDetailsDialog">
                <span class="white--text">
                    <v-icon left>mdi-pencil</v-icon>
                    {{ $t('subscription_page.change_bank_details') }}
                </span>
            </v-btn>
            <v-btn elevation="0" color="primary" rounded @click="openViewHistoryDialog">
                <span class="white--text">
                    <v-icon left>mdi-history</v-icon>
                    {{ $t('subscription_page.history') }}
                </span>
            </v-btn>
        </div>

        <div v-if="loading" class="text-center pa-8">
            <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else class="subscription-content pa-4">
            <v-row>
                <v-col v-for="(device, index) in devicesData" :key="index" class="pa-2" cols="12" lg="4" md="6" sm="6">
                    <v-card class="v-card v-sheet theme--light elevation-0 desktop-asset-card h-100 pointer">
                        <v-card-text class="h-100 d-flex flex-column">
                            <div class="d-flex justify-space-between">
                                <div>
                                    <div class="d-flex align-center gap-2 mb-3">
                                        <span class="mb-0 font-weight-bold font-18 text-overflow-ellipsis primary--text">
                                            {{ device.asset_name }}
                                        </span>
                                        <v-chip
                                            :color="!isSubscriptionExpired(device) ? 'success' : 'error'"
                                            small
                                            class="font-weight-bold"
                                        >
                                            <div class="d-flex align-center white--text">
                                                <v-icon left color="white" size="16">
                                                    {{ !isSubscriptionExpired(device) ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                                </v-icon>
                                                {{
                                                    !isSubscriptionExpired(device)
                                                        ? $t('subscription_page.active')
                                                        : $t('subscription_page.inactive')
                                                }}
                                            </div>
                                        </v-chip>
                                    </div>

                                    <div class="subscription-details">
                                        <div class="mb-1">
                                            <span class="font-12 font-color-medium">
                                                <v-icon size="20">mdi mdi-flag-outline</v-icon>
                                                {{ $t('subscription_page.subscription_start') }}
                                            </span>
                                            <v-chip v-if="device.date_subscription_start" color="grey" small class="ml-2 font-12" outlined>
                                                {{ moment(device.date_subscription_start).format('DD/MM/YYYY') }}
                                            </v-chip>
                                        </div>

                                        <div class="mb-1">
                                            <span
                                                :class="['font-12 font-color-medium', !isSubscriptionExpired(device) ? '' : 'error--text']"
                                            >
                                                <v-icon size="20" :color="!isSubscriptionExpired(device) ? '' : 'error'">
                                                    {{ device.automatic_renewal ? 'mdi-currency-usd' : 'mdi-clock-alert-outline' }}
                                                </v-icon>
                                                {{
                                                    $t(
                                                        device.automatic_renewal
                                                            ? 'subscription_page.next_payment'
                                                            : !isSubscriptionExpired(device)
                                                            ? 'subscription_page.expire_at'
                                                            : 'subscription_page.expirate'
                                                    )
                                                }}
                                            </span>
                                            <v-chip v-if="device.date_subscription_end" color="grey" small class="ml-2 font-12" outlined>
                                                <span class="font-color-normal">
                                                    {{ moment(device.date_subscription_end).format('DD/MM/YYYY') }}
                                                </span>
                                            </v-chip>
                                        </div>

                                        <div class="mb-1">
                                            <span class="font-12 font-color-medium">
                                                <v-icon size="20">mdi-currency-usd</v-icon>
                                                {{ $t('subscription_page.subscription') }}
                                            </span>
                                            <span class="font-12 font-weight-bold font-color-default ml-2">
                                                {{ device.price }} €/ {{ $t('year') }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="d-flex flex-column align-center justify-end mr-4" style="height: 100%">
                                    <img
                                        v-if="device.path_image_device_referential"
                                        :src="device.path_image_device_referential"
                                        :alt="device.device_number"
                                        height="40"
                                        class="mb-1"
                                    />
                                    <v-icon v-else size="40" color="primary">mdi-access-point-network</v-icon>
                                    <span class="font-12 font-weight-bold font-color-default mt-1">
                                        {{ device.device_number }}
                                    </span>
                                </div>
                            </div>

                            <div class="mt-4" v-if="displaySubscriptionManagement">
                                <div v-if="device.automatic_renewal" class="text-center">
                                    <v-btn
                                        block
                                        elevation="0"
                                        text
                                        class="text-capitalize"
                                        rounded
                                        @click="openStopRenewalDialog(device)"
                                        color="blue"
                                    >
                                        <span class="blue--text">
                                            {{ $t('subscription_page.stop_automatic_renewal_btn') }}
                                        </span>
                                    </v-btn>
                                </div>

                                <v-btn
                                    v-else-if="!isSubscriptionExpired(device)"
                                    color="warning"
                                    class="font-weight-bold"
                                    block
                                    elevation="0"
                                    @click="openRenewDialog(device)"
                                    rounded
                                >
                                    {{ $t('subscription_page.renew_automatically_btn') }}
                                </v-btn>
                                <v-btn
                                    v-else
                                    color="error"
                                    block
                                    elevation="0"
                                    class="font-weight-bold"
                                    @click="openResumeDialog(device)"
                                    rounded
                                >
                                    <span class="white--text">
                                        {{ $t('subscription_page.resume_subscription') }}
                                    </span>
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col v-if="devicesData.length === 0" cols="12" class="text-center pa-8">
                    <p class="font-16 font-color-medium">
                        {{ $t('subscription_page.subscription_paid_by') }} {{ deskName }}
                    </p>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script>
import SubscriptionRepository from '@/repositories/SubscriptionRepository'
import SubscriptionConfirmationDialogComponent from './SubscriptionConfirmationDialogComponent.vue'

export default {
    name: 'ClientSubscriptionComponent',

    components: {
        SubscriptionConfirmationDialogComponent,
    },

    data() {
        return {
            loading: true,
            loading_action: false,
            devicesData: [],
            displaySubscriptionManagement: false,
            activeSubscriptionsCount: 0,
            dialogRenew: false,
            dialogStopRenewal: false,
            dialogResume: false,
            dialogChangeBankDetails: false,
            dialogHistoryDetails: false,
            selectedDevicePrice: '',
            selectedDeviceName: '',
            selectedDeviceNumber: '',
            selectedDeviceUid: '',
            hasSubscription: false,
            deskName: '',
        }
    },
    computed: {
        selectedDate() {
            return this.moment().format('DD/MM/YYYY')
        },
    },

    created() {
        this.loadSubscriptions()
    },

    methods: {
        handleError(error) {
            if (typeof this.manageError === 'function') {
                this.manageError(error)
            } else {
                console.error(error)
            }
        },

        redirectToUrl(url) {
            if (url) {
                window.location.href = url
            }
        },

        openDialog(dialogName) {
            this[dialogName] = true
        },

        closeDialog(dialogName, resetData = false) {
            this[dialogName] = false
            if (resetData) {
                this.resetSelectedDevice()
            }
        },

        resetSelectedDevice() {
            this.selectedDeviceNumber = ''
            this.selectedDeviceUid = ''
            this.selectedDeviceName = ''
            this.selectedDevicePrice = ''
        },

        setSelectedDevice(device) {
            this.selectedDeviceName = device.asset_name || ''
            this.selectedDeviceNumber = device.device_number || ''
            this.selectedDeviceUid = device.uid || ''
            this.selectedDevicePrice = device.price || ''
        },

        openChangeBankDetailsDialog() {
            this.openDialog('dialogChangeBankDetails')
        },

        closeChangeBankDetailsDialog() {
            this.closeDialog('dialogChangeBankDetails')
        },

        openViewHistoryDialog() {
            this.openDialog('dialogHistoryDetails')
        },

        closeViewHistoryDialog() {
            this.closeDialog('dialogHistoryDetails')
        },

        openRenewDialog(device) {
            this.setSelectedDevice(device)
            this.openDialog('dialogRenew')
        },

        closeRenewDialog() {
            this.closeDialog('dialogRenew', true)
        },

        openStopRenewalDialog(device) {
            this.selectedDeviceNumber = device.device_number
            this.selectedDeviceUid = device.uid
            this.selectedDeviceName = device.asset_name
            this.openDialog('dialogStopRenewal')
        },

        closeStopRenewalDialog() {
            this.closeDialog('dialogStopRenewal', true)
        },

        openResumeDialog(device) {
            this.setSelectedDevice(device)
            this.openDialog('dialogResume')
        },

        closeResumeDialog() {
            this.closeDialog('dialogResume', true)
        },

        isSubscriptionExpired(device) {
            return !device.automatic_renewal && !device.is_subscription_active
        },

        loadSubscriptions() {
            this.loading = true

            SubscriptionRepository.getSubscription()
                .then((response) => {
                    const data = response.data.data
                    this.devicesData = data.devicesData || []
                    this.displaySubscriptionManagement = !!data.displaySubscriptionManagement
                    this.activeSubscriptionsCount = data.totalSubscriptions || 0
                    this.hasSubscription = data.devicesData && data.devicesData.length > 0
                    this.deskName = data.professionalName || ''
                })
                .catch((error) => {
                    this.handleError(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        confirmChangeBankDetails() {
            this.loading_action = true

            SubscriptionRepository.getUpdateBankDetails()
                .then((response) => {
                    const checkoutUrl = response.data.data?.checkout_url
                    this.redirectToUrl(checkoutUrl)
                    this.closeChangeBankDetailsDialog()
                })
                .catch((error) => {
                    this.handleError(error)
                })
                .finally(() => {
                    this.loading_action = false
                })
        },

        confirmViewHistoryDetails() {
            this.loading_action = true

            SubscriptionRepository.getCustomerPaymentHistory()
                .then((response) => {
                    const portalUrl = response.data.data?.portal_url
                    this.redirectToUrl(portalUrl)
                    this.closeViewHistoryDialog()
                })
                .catch((error) => {
                    this.handleError(error)
                })
                .finally(() => {
                    this.loading_action = false
                })
        },

        confirmRenewSubscription() {
            this.loading_action = true

            SubscriptionRepository.renewSubscription(this.selectedDeviceUid)
                .then((response) => {
                    const checkoutUrl = response.data.data?.checkout_url
                    if (!checkoutUrl) {
                        throw new Error('No checkout URL received')
                    }
                    this.closeRenewDialog()
                    this.redirectToUrl(checkoutUrl)
                })
                .catch((error) => {
                    this.handleError(error)
                })
                .finally(() => {
                    this.loading_action = false
                })
        },

        confirmStopAutomaticRenewal() {
            this.loading_action = true

            SubscriptionRepository.stopAutomaticRenewal(this.selectedDeviceUid)
                .then(() => {
                    if (typeof this.showSnackbar === 'function') {
                        this.showSnackbar('success', this.$t('subscription_page.stop_renewal_success'))
                    }
                    this.loadSubscriptions()
                    this.closeStopRenewalDialog()
                })
                .catch((error) => {
                    this.handleError(error)
                })
                .finally(() => {
                    this.loading_action = false
                })
        },

        confirmResumeSubscription() {
            this.loading_action = true

            SubscriptionRepository.resumeSubscription(this.selectedDeviceUid)
                .then((response) => {
                    const checkoutUrl = response.data.data?.checkout_url
                    if (!checkoutUrl) {
                        throw new Error('No checkout URL received')
                    }
                    this.closeResumeDialog()
                    this.redirectToUrl(checkoutUrl)
                })
                .catch((error) => {
                    this.handleError(error)
                })
                .finally(() => {
                    this.loading_action = false
                })
        },
    },
}
</script>
<style scoped>
.no-bg-btn {
    background-color: transparent !important;
    box-shadow: none !important;
}

.no-bg-btn:hover,
.no-bg-btn:focus {
    background-color: transparent !important;
    box-shadow: none !important;
}
</style>
